import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getServices,
  getVirtualCategories,
  categoryNameToSlug,
  Service,
  VirtualCategory,
} from "../api";

interface SubItem {
  name: string;
  parentCategoryName: string;
}

export default function SubcategoryListPage() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [pageIcon, setPageIcon] = useState("📋");
  const [pageDescription, setPageDescription] = useState("");
  const [subs, setSubs] = useState<SubItem[]>([]);

  useEffect(() => {
    if (!categorySlug) return;

    Promise.all([getServices(), getVirtualCategories()])
      .then(([services, virtuals]) => {
        // First check if this slug matches a virtual category
        const virtual = virtuals.find(
          (v) => v.virtualCategoryId === categorySlug
        );
        if (virtual) {
          setPageTitle(virtual.name);
          setPageIcon(virtual.icon || "🔧");
          setPageDescription(virtual.description || "");
          setSubs(
            (virtual.subcategoryRefs || []).map((ref) => ({
              name: ref.subcategoryName,
              parentCategoryName: ref.categoryName,
            }))
          );
          return;
        }

        // Otherwise look up real category by slug
        const real = services.find(
          (s) => categoryNameToSlug(s.name) === categorySlug
        );
        if (real) {
          setPageTitle(real.name);
          setPageIcon(real.icon || "📋");
          setSubs(
            (real.subcategories || []).map((sub) => ({
              name: sub,
              parentCategoryName: real.name,
            }))
          );
          return;
        }

        setError("Category not found.");
      })
      .catch((err) => {
        console.error(err);
        setError("Couldn't load this category.");
      })
      .finally(() => setLoading(false));
  }, [categorySlug]);

  function handleSubcategoryClick(sub: SubItem) {
    // Phase 2: guests can now browse pros for the subcategory.
    // Booking still requires login (Phase 3).
    const subSlug = categoryNameToSlug(sub.name);
    navigate(`/browse/${categorySlug}/${subSlug}`);
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/browse")}
            className="text-sm text-mouna-blue font-bold"
          >
            ← Browse
          </button>
          <button
            onClick={() => navigate("/login")}
            className="text-sm font-bold text-mouna-blue"
          >
            Sign in
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {loading && (
          <div className="text-center text-gray-400 text-sm py-16">Loading…</div>
        )}
        {error && (
          <div className="text-center text-red-600 text-sm py-8">
            {error}
            <div className="mt-4">
              <button
                onClick={() => navigate("/browse")}
                className="text-mouna-blue font-bold"
              >
                ← Back to all services
              </button>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl">{pageIcon}</span>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-navy">
                {pageTitle}
              </h1>
            </div>
            {pageDescription && (
              <p className="text-sm text-gray-600 mb-6">{pageDescription}</p>
            )}

            <p className="text-sm text-gray-500 mb-4">
              Choose a service to find a pro
            </p>

            <div className="space-y-2">
              {subs.map((sub, i) => (
                <button
                  key={`${sub.parentCategoryName}-${sub.name}-${i}`}
                  onClick={() => handleSubcategoryClick(sub)}
                  className="w-full flex items-center justify-between gap-3 p-4 rounded-xl bg-white hover:shadow-md transition-all border border-gray-100 text-left"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-navy text-sm">
                      {sub.name}
                    </div>
                    {sub.parentCategoryName !== pageTitle && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        in {sub.parentCategoryName}
                      </div>
                    )}
                  </div>
                  <span className="text-mouna-blue font-bold text-sm">
                    Find a pro →
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
